import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { Text } from '@dealstack/frontend/shared/components/fundament/Text/Text';
import { memo } from 'react';
import styled from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import type { IButtonProps } from './Button.types';

const Button = ({
	className,
	children,
	size = 'medium',
	icon,
	iconPlacement,
	iconSize = 'md',
	buttonType = 'button',
	isDisabled,
	...props
}: IButtonProps) => {
	const isText = typeof children === 'string';

	const renderIcon = () => {
		if (!icon) {
			return null;
		}
		return (
			<IconWrapper>
				{typeof icon === 'string' ? (
					<Icon size={iconSize} variant={icon as PredefinedIcons} />
				) : (
					icon
				)}
			</IconWrapper>
		);
	};

	return (
		<button
			disabled={isDisabled}
			type={buttonType}
			className={`${className} clickAnimation`}
			{...props}
		>
			{iconPlacement === 'left' && renderIcon()}
			{isText ? (
				<Text elipsis variant={size === 'medium' ? 'button' : 'button-small'} bold>
					{children}
				</Text>
			) : (
				children
			)}
			{iconPlacement === 'right' && renderIcon()}
		</button>
	);
};

const IconWrapper = styled.div`
	display: contents;
`;

const Memoized = memo(Button);
const Styled = styled(Memoized)`
	border: none;
	cursor: pointer;
	padding: 0.5rem 1rem;
	border-radius: 4px;
	outline: none !important;
	display: flex;
	flex-direction: row;
	align-items: center;
	justify-content: center;
	text-align: center;
	transition: all 0.05s linear;
	border: 1px solid transparent;
	height: fit-content;

	&:disabled {
		box-shadow: none;
		cursor: not-allowed;
	}

	${({ size = 'medium', icon, iconPlacement }) => {
		if (!icon) {
			return ``;
		}
		if (iconPlacement === 'left') {
			return `
              ${IconWrapper} > * {
                padding-right: ${size === 'medium' ? '0.7rem' : '0.5rem'} ;
              }
            `;
		}
		return `
             ${IconWrapper} > * {
                padding-left: ${size === 'medium' ? '0.7rem' : '0.5rem'} ;
              }
        `;
	}}

	${({ variant, theme }) => {
		switch (variant) {
			case 'secondary': {
				return `
                    background-color: ${theme.v2.color.primary[600]};
                    ${theme.shadow.z4}

                    &:hover {
                        background-color: ${theme.v2.color.primary[800]};
                        box-shadow: none;
                    }

                    &:disabled {
                        background-color: ${theme.v2.color.neutral[300]};
                        color: ${theme.v2.color.neutral[500]};
                    }
                `;
			}
			case 'primary': {
				return `
                    background-color: ${theme.v2.color.secondary[800]};
                    color: ${theme.v2.color.neutral[100]};

                    &:hover {
                        background-color: ${theme.v2.color.secondary[1000]};
                        box-shadow: none;
                    }

                    &:disabled {
                        background-color: ${theme.v2.color.neutral[300]};
                        color: ${theme.v2.color.neutral[500]};
                    }
                `;
			}
			case 'outlined': {
				return `
                    background-color: transparent;
                    border: 1px solid ${theme.v2.color.secondary[800]};
                    color: ${theme.v2.color.secondary[800]};
                    ${Text} {
                        font-weight: 500;
                    }

                    &:hover {
                        background-color: ${theme.v2.color.secondary[200]};
                    }

                    &:disabled {
                        background-color: transparent;
                        border: 1px solid ${theme.v2.color.neutral[400]};
                        color: ${theme.v2.color.neutral[400]};
                    }
                `;
			}
			case 'text': {
				return `
                    background-color: transparent;
                    color: ${theme.v2.color.secondary[800]};
                    ${Text} {
                        font-weight: 500;
                    };

                    &:hover {
                        background-color: ${theme.v2.color.secondary[200]};
                    }

                    &:disabled {
                        background-color: transparent;
                        color: ${theme.v2.color.neutral[500]};
                    }
                `;
			}
			case 'table-text': {
				return `
                    background-color: transparent;
                    padding: 0;
                    color: ${theme.v2.color.secondary[800]};

                    &:hover {
                        background-color: ${theme.v2.color.secondary[200]};
                    }

                    &:disabled {
                        background-color: transparent;
                        color: ${theme.v2.color.neutral[500]};
                    }
                `;
			}
			case 'project': {
				return `
                    background-color: ${theme.v2.color.neutral[200]};
                    color: ${theme.v2.color.secondary[1000]};
                    border: 1px solid ${theme.v2.color.neutral[300]};

                    ${Icon}.svg {
                        color: ${theme.v2.color.secondary[1000]};
                    }

                    &:hover {
                        background-color: ${theme.v2.color.secondary[200]};
                    }

                    &:disabled {
                        background-color: transparent;
                    }
                `;
			}
			case 'delete': {
				return `
                    background-color: ${theme.v2.color.error[1000]};
                    color: ${theme.v2.color.white[500]};

                    &:hover {
                        background-color: ${theme.v2.color.error[800]};
                    }

                    &:disabled {
                        background-color: ${theme.v2.color.neutral[300]};
                        color: ${theme.v2.color.neutral[500]};
                    }
                `;
			}
			default:
				return ``;
		}
	}}

    ${({ size = 'medium' }) => {
		return size === 'small'
			? `
                padding: 0.313rem 1rem;
            `
			: ``;
	}}

    ${({ rounded }) => (rounded ? 'border-radius: 1.3rem' : '')}
`;

export { Styled as Button };
